import { computed, onUnmounted, ref, watch } from 'vue'
import useCurrentCast from './currentCast'
import store from '@/store'
import { useAddLog } from '@/modules/logging/compositions'

const { currentCast } = useCurrentCast()
const { addLogNoWait } = useAddLog()

const useOnlineSessionWatchDog = () => {
  let timerId: ReturnType<typeof setTimeout>|null = null
  const userId = computed(() => store.state.user.id)
  const sessionId = computed(() => store.state.user.sessionId)
  const isSessionIdInCast = computed(() => {
    if (!userId.value || !currentCast.value) return false
    const userOnlineSessions = currentCast.value.online_sessions[userId.value]
    if (!userOnlineSessions) return false
    return userOnlineSessions.includes(sessionId.value)
  })
  const isInCast = computed(() => store.state.events.inCast)
  const sessionHasBeenInCast = ref(false)

  watch([userId, sessionId, isSessionIdInCast], () => {
    if (userId.value && sessionId.value && isSessionIdInCast.value) sessionHasBeenInCast.value = true
    else if (!userId.value || !sessionId.value) sessionHasBeenInCast.value = false
  }, { immediate: true })

  let timeoutBackoffDelay = 2000
  const delayedReconnect = async () => {
    const logMsg = `Session id ${sessionId.value} reconnecting RTDB`
    addLogNoWait(logMsg, 'warning')
    console.warn(logMsg)
    await store.state.user.presence?.reconnect()
    if (timerId !== null) {
      if (timeoutBackoffDelay <= 2000) timeoutBackoffDelay = 3000
      else if (timeoutBackoffDelay <= 3000) timeoutBackoffDelay = 5000
      else timeoutBackoffDelay += 5000
      timerId = setTimeout(delayedReconnect, timeoutBackoffDelay)
    }
  }

  // If we notice here that our session is no longer present in the cast's `online_sessions`, it means that we're still
  // receiving updates from Firestore, but somehow our Firebase Realtime DB connection has (remained) disconnected. So
  // try to trigger a reconnect to the RTDB.
  // Notes:
  // - We delay the initial reconnect attempt by a few seconds to give the RTDB some time to recover on its own. For
  //   example if there is short network interruption, and our session gets removed, but the RTDB reconnects on its own.
  //   But `isSessionIdInCast` might get temporarily marked here as `false` before the session is re-added to the
  //   `online_sessions`. Which, without some delay, would immediately trigger an, in that case, unnecessary reconnect.
  // - We linearly increase the delay between reconnect attempts to not try to reconnect too much.
  // - The `timerId` is also used to signal to the timeout callback that it shouldn't re-trigger itself (by setting its
  //   value to `null`).
  // - The `sessionHasBeenInCast` variable is used to prevent triggering a reconnect when we initially enter the cast
  //   and our session hasn't been added yet to the cast (due to our FirebasePresence still being set up).
  const hasSessionGoneMissing = computed(() => !isSessionIdInCast.value && sessionHasBeenInCast.value && isInCast.value)

  watch(hasSessionGoneMissing, (value) => {
    if (value && timerId === null) {
      addLogNoWait(`Session id ${sessionId.value} has gone missing. Attempting RTDB reconnect in 2s...`, 'warning')
      console.warn(`Session id ${sessionId.value} has gone missing. Attempting RTDB reconnect in 2s...`)
      timerId = setTimeout(delayedReconnect, timeoutBackoffDelay)
    } else if (!value && timerId !== null) {
      addLogNoWait(`Session id ${sessionId.value} in cast again. Aborting scheduled reconnect`, 'warning')
      console.warn(`Session id ${sessionId.value} in cast again. Aborting scheduled reconnect`)
      clearTimeout(timerId)
      timerId = null
      timeoutBackoffDelay = 2000
    }
  })

  const casterContentIdInCast = computed(() => {
    if (!userId.value || !currentCast.value) return null
    const casterSceneId = currentCast.value.getFirstSceneIdWithCaster(userId.value)
    if (!casterSceneId) return null
    return currentCast.value.getCasterContentIdForSceneId(userId.value , casterSceneId)
  })

  watch(casterContentIdInCast, async (newVal: string|null, oldVal: string|null) => {
    addLogNoWait(`My content id changed from ${oldVal} to ${newVal} (session id: ${sessionId.value})`, 'warning')
    if (!newVal || store.state.usermedia.streamDocId === newVal || !store.state.events.casting) return
    addLogNoWait(`(re)connecting stream reference (session id: ${sessionId.value})`, 'warning')
    await store.dispatch.usermedia.connectStreamReference()
  })

  let streamReAddTimerId: ReturnType<typeof setTimeout>|null = null
  const streamHasBeenPresent = ref(false)
  const streamId = computed(() => store.state.usermedia.streamDocId)
  const isConnected = computed(() => store.state.usermedia.isConnected)
  const streamDocIsPresent = computed(() => {
    return streamId.value && isConnected.value && Object.keys(store.state.events.streamDocs).includes(streamId.value)
  })

  watch([streamId, isConnected, streamDocIsPresent], () => {
    if (streamId.value && streamDocIsPresent.value) streamHasBeenPresent.value = true
    else if (!streamId.value || !isConnected.value) streamHasBeenPresent.value = false
  }, { immediate: true })

  const isStreamDocGone = computed(() => !streamDocIsPresent.value && streamHasBeenPresent.value)

  const delayedStreamReAdd = async () => {
    streamReAddTimerId = null
    if (!streamId.value || !userId.value) {
      addLogNoWait('Cannot re-add stream without stream id or user id', 'error')
      return
    }
    const streamAddResult = await store.dispatch.usermedia.addStreamDocToDb({
      docId: streamId.value,
      userId: userId.value
    })
    if (streamAddResult.isFailure) {
      addLogNoWait(
        `Failed to re-add stream ${streamId.value} for my user ${userId.value} to DB: ${streamAddResult.message}`,
        'error'
      )
    } else {
      addLogNoWait(`Re-added stream ${streamId.value} for my user ${userId.value}`, 'info')
    }
  }

  watch(isStreamDocGone, (newValue: boolean) => {
    if (newValue && streamReAddTimerId === null) {
      streamReAddTimerId = setTimeout(delayedStreamReAdd, 1000)
    } else if (!newValue && streamReAddTimerId !== null) {
      clearTimeout(streamReAddTimerId)
      streamReAddTimerId = null
    }
  })

  onUnmounted(() => {
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
  })

  return
}

export default useOnlineSessionWatchDog
